import { useMemo } from 'react'

import envConfig from '~/configs/env'
import type { UserModel } from '~/data/api/client/fleet-management'
import { useGetUserQuery } from '~/data/api/client/fleet-management'
import type { SynopUser } from '~/data/types/user'
import { useAuth } from '~/hooks/useAuth'
import { useCurrentOrgId } from '~/store/slices/currentOrg'

import { useGetOrgUsers } from './organizations'
import type { UseQueryOptions } from './polling'
import { combineSkip, useTransformedDict } from './utils'

type MaybeSynopUser = SynopUser | undefined

/** Same prefixes as vehicle/OCPP tag maps so driver RFID lookups match charger idTag variants. */
const AUTH_TAG_PREFIXES = ['VID:', 'EVCCID:']

export function useUser(userId?: string, options?: UseQueryOptions) {
  const result = useGetUserQuery({ id: userId ?? '' }, combineSkip(!userId, options))

  // Ensure the user returned from the hook matches the ID passed in. This is needed when the ID
  // changes or is cleared.
  const user = (result.data?.id === userId ? result.data : undefined) as MaybeSynopUser
  return { user, ...result }
}

export function useUserTokenMap(usersInput?: UserModel[]) {
  const currentOrgId = useCurrentOrgId()
  const { users: usersFetched, ...result } = useGetOrgUsers(currentOrgId)

  const withAuthToken = useMemo(() => {
    const users = usersInput ?? usersFetched ?? []
    return users
      .filter((user) => user.rfidTags?.length ?? 0 > 0)
      .flatMap((user) =>
        user.rfidTags!.map((tag) => ({
          [tag.ocppTagValue as string]: user
        }))
      )
      .reduce((acc, curr) => ({ ...acc, ...curr }), {})
  }, [usersInput, usersFetched])

  return {
    userAuthTokens: useTransformedDict(withAuthToken, {
      caseInsensitive: true,
      prefix: AUTH_TAG_PREFIXES,
      separator: ':'
    }),
    ...result
  }
}

export function useCurrentUser() {
  const { user } = useAuth()
  const { user: currentUser, isLoading: isUserLoading } = useUser(user?.id)

  const isAdmin = useMemo(() => {
    return currentUser?.roles?.includes('Admin')
  }, [currentUser])

  return {
    synopUser: currentUser,
    isUserLoading,
    isAdmin
  }
}

export function useLanguage() {
  const { synopUser } = useCurrentUser()
  return synopUser?.preferredLanguage ?? envConfig.defaultLanguage
}
